import { StatePage } from "@/components/state-page";
import { isDefinedError } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { requirePermissions, useHasPermission } from "@/utils/permissions";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  notFound,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { t } from "i18next";
import TripForm from "../../-form/-index";
import { TripFormSkeleton } from "../../-form/-skeleton";

export const Route = createFileRoute("/_authenticated/trips/$id/edit/")({
  beforeLoad: requirePermissions("trip:update"),
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(
      orpc.trip.get.queryOptions({ input: { id: params.id } }),
    );
  },
  component: RouteComponent,
  errorComponent: ({ error, reset }) => (
    <StatePage
      error={error}
      reset={reset}
      resourceType="trips.edit"
      emptyResourceType={undefined}
    />
  ),
});

function RouteComponent() {
  const params = Route.useParams();
  const navigate = useNavigate();
  const context = useRouteContext({ from: "/_authenticated" });
  const hasTripPermission = useHasPermission("trip:get");

  const updateMutation = useMutation(
    orpc.trip.update.mutationOptions({
      onError: async (error) => {
        if (isDefinedError(error) && error.code === "NOT_FOUND") {
          throw notFound();
        }

        if (
          isDefinedError(error) &&
          (error.code === "FORBIDDEN" || error.code === "UNAUTHORIZED")
        ) {
          notifications.show({
            title: t("forbidden.title"),
            message: t("trips.edit.forbidden.description"),
            color: "red",
          });
          return;
        }

        notifications.show({
          title: error.message || t("unknownError"),
          message: t("trips.edit.error.description"),
          color: "red",
        });
      },
    }),
  );

  const {
    data: trip,
    isLoading,
    error,
    refetch,
  } = useQuery(orpc.trip.get.queryOptions({ input: { id: params.id } }));

  if (isLoading) {
    return <TripFormSkeleton />;
  }

  if (error) {
    return (
      <StatePage
        error={error}
        reset={refetch}
        resourceType="trips.edit"
        emptyResourceType={undefined}
      />
    );
  }

  if (!trip) {
    return null;
  }

  return (
    <TripForm
      mode="edit"
      trip={trip}
      onSubmit={async ({ payload, form, existingImageKeys, newImages }) => {
        const result = await updateMutation.mutateAsync({
          ...payload,
          id: params.id,
          existingImageKeys,
          newImages,
        });

        form.reset();

        await context.queryClient.invalidateQueries({
          queryKey: orpc.trip.get.key({ input: { id: result.id } }),
        });

        if (hasTripPermission) {
          void navigate({
            to: "/trips/$id",
            params: { id: result.id },
          });
        }

        notifications.show({
          title: t("success"),
          message: t("trips.edit.success.description"),
          color: "green",
        });
      }}
    />
  );
}
